import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import FormInput from '../components/inputs/FormInput'
import DropdownInput from '../components/inputs/DropdownInput'
import DatePickerInput2 from '../components/inputs/DatePickerInput2'
import DropdownMultiInput from '../components/inputs/DropdownMultiInput'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import dayjs from 'dayjs'
import 'react-toastify/dist/ReactToastify.css'

const EditEntity = () => {
    const { id } = useParams()

    const [harbors, setHarbors] = useState([])
    const [harborSkills, setHarborSkills] = useState([])

    const [selectedHarbors, setSelectedHarbors] = useState(null)
    const [selectedHarborsError, setSelectedHarborsError] = useState(null)

    const [selectedState, setSelectedState] = useState(null)
    const [selectedStateError, setSelectedStateError] = useState(null)

    const [date, setDate] = useState(null)
    const [dateError, setDateError] = useState(null)

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(null)

    const [nif, setNif] = useState('')
    const [nifError, setNifError] = useState(null)

    const [concession, setConcession] = useState('')
    const [concessionError, setConcessionError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.ADMINIT) {
                navigate('/')
            } else {
                getHarbors()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get harbors
    const getHarbors = () => {
        fetch('/api/Backoffice/ListHarbors', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                const arrayHarbors = jsonRes.map((option) => ({ value: option.id, label: option.name }))

                setHarbors(arrayHarbors)

                getEntityDetails(arrayHarbors)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setHarbors([])
                }, 1000)
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setHarbors([])
            }, 1000)   
        })
    }
    
    // Handle entity details
    const getEntityDetails = (arrayHarbors) => {
        fetch(`/api/Backoffice/${id}/DetailsEntity`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()

                    let harborsList = []

                    jsonRes.harborSkills.map((harbor) => {
                        harborsList.push(harbor.harborId)
                    })

                    setSelectedHarbors(arrayHarbors.filter((item) => harborsList.includes(item.value)))
                    setSelectedState(jsonRes.active ? Constants.STATES[0] : Constants.STATES[1])
                    setDate(dayjs(jsonRes.date))
                    setName(jsonRes.name)
                    setNif(jsonRes.nif.toString())
                    setConcession(jsonRes.contractor)

                    setHarborSkills(jsonRes.harborSkills)
                } else {
                    showErrorToast(strings.getEntityDetailsError)
                    resetVariables()
                }
                
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                resetVariables()
            }, 1000)
        })
    }

    // Handle changes in harbors dropdown
    const onChangeSelectedHarbors = (item) => {
        setSelectedHarbors(item)
        setSelectedHarborsError(null)
    }

    // Handle changes in state dropdown
    const onChangeSelectedState = (item) => {
        setSelectedState(item)
        setSelectedStateError(null)
    }

     // Handle changes in date input
     const onChangeDate = (selectedDate) => {
        if(selectedDate) {
            setDate(selectedDate)
        }
    }

    // Handle when date is selected in date input
    const onAcceptDate = (selectedDate) => {
        setDateError(null)
        setDate(selectedDate)
    }

    // Handle errors in date input
    const onErrorDate = (error) => {
        if(error) {
            setDateError(strings.invalidDate)
        } else {
            setDateError(null)
        }
    }

    // Handle changes in name input text
    const onChangeName = (nameText) => {
        setNameError(null)
        setName(nameText.target.value)
    }

    // Handle changes in nif input text
    const onChangeNif = (nifText) => {
        setNifError(null)
        setNif(nifText.target.value)
    }

    // Handle changes in concession input text
    const onChangeConcession = (concessionText) => {
        setConcessionError(null)
        setConcession(concessionText.target.value)
    }

    // Check if NIF is valid
    const isValidNIF = (nif) => {
        return /^\d+$/.test(nif)
    }

    // Check if all fields are valid and navigate to edit entity skills screen
    const goToEditEntitySkills = () => {
        let hasErrors = false

        if (name.trim().length === 0) {
            setNameError(strings.insertNameError)
            hasErrors = true
        }

        if (nif.trim().length === 0) {
            hasErrors = true
            setNifError(strings.insertNifError)
        } else if (!isValidNIF(nif)) {
            hasErrors = true
            setNifError(strings.invalidNif)
        } else if (nif.trim().length < 9) {
            hasErrors = true
            setNifError(strings.invalidNif)
        }

        if (concession.trim().length === 0) {
            hasErrors = true
            setConcessionError(strings.insertConcessionError)
        }

        if(!selectedHarbors || selectedHarbors.length === 0) {
            hasErrors = true
            setSelectedHarborsError(strings.selectAffectedShipyardError2)
        }

        if(!selectedState) {
            hasErrors = true
            setSelectedStateError(strings.selectStateError)
        }

        if(!date) {
            hasErrors = true
            setDateError(strings.selectDateError)
        }

        if (!hasErrors) {
            const selectedDate = new Date(date)
            selectedDate.setHours(new Date().getHours())

            navigate('/edit-entity-skills', {
                state: {
                    entityData: {
                        entityId: id,
                        name: name,
                        nif: nif,
                        harbors: selectedHarbors,
                        harborSkills: harborSkills,
                        contractor: concession,
                        active: selectedState.value === 1 ? true : false,
                        date: selectedDate.toISOString()
                    }
                }}
            )
        }
    }

    // Resets local state variables
    const resetVariables = () => {
        setSelectedHarbors(null)
        setSelectedState(null)
        setDate(null)
        setName('')
        setNif('')
        setConcession('')
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            { isLoading ?
                <div className='h-screen'>
                    <LoadingModal />
                </div>
            :   <div className='flex flex-col flex-1 px-[12.5rem] min-h-screen mb-6'>
            
                    <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                        <button onClick={() => navigate(-1)}>
                            <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                        </button>
                        <p className='font-montserrat font-semibold text-accent'>{strings.entityManagement} / {strings.updateEntity}</p>
                    </div>

                    <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>
                    <p className='font-montserrat text-sm mt-2'>{strings.newEntityInfo}</p>

                    <div className='flex flex-row mt-8 space-x-6'>
                        <div className='flex flex-1 flex-col'>
                            <label htmlFor='name' className='font-montserrat font-semibold text-sm'>{strings.name2}</label>
                            <FormInput 
                                id='name' 
                                type='text'
                                value={name}
                                placeholder={strings.insertName2}
                                onChange={onChangeName}
                                error={nameError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nameError ? nameError : ''}</p>
                        </div>

                        <div className='flex flex-1 flex-col'>
                            <label htmlFor='nif' className='font-montserrat font-semibold text-sm'>{strings.nif}</label>
                            <FormInput 
                                id='nif' 
                                type='text'
                                value={nif}
                                placeholder={strings.insertNif}
                                onChange={onChangeNif}
                                error={nifError}
                                maxLength={9}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nifError ? nifError : ''}</p>
                        </div>
                    </div>

                    <div className='flex flex-row mt-9 space-x-6'>
                        <div className='flex flex-1 flex-col'>
                            <p className='font-montserrat font-semibold text-sm'>{strings.harbors}</p>
                            <DropdownMultiInput 
                                options={harbors}
                                value={selectedHarbors}
                                onChange={onChangeSelectedHarbors}
                                error={selectedHarborsError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedHarborsError ? selectedHarborsError : ''}</p>
                        </div>

                        <div className='flex flex-1 flex-col'>
                            <label htmlFor='concession' className='font-montserrat font-semibold text-sm'>{strings.concession}</label>
                            <FormInput 
                                id='concession' 
                                type='text'
                                value={concession}
                                placeholder={strings.insertConcession}
                                onChange={onChangeConcession}
                                error={concessionError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{concessionError ? concessionError : ''}</p>
                        </div>
                    </div>

                    <div className='flex flex-row mt-9 space-x-6'>
                        <div className='flex flex-1 flex-col'>
                            <p className='font-montserrat font-semibold text-sm'>{strings.state}</p>
                            <DropdownInput 
                                options={Constants.STATES}
                                value={selectedState}
                                onChange={onChangeSelectedState}
                                error={selectedStateError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedStateError ? selectedStateError : ''}</p>
                        </div>

                        <div className='flex flex-1 flex-col'>
                            <label htmlFor='concession' className='font-montserrat font-semibold text-sm mb-1.5'>{strings.startDateConcession}</label>
                            <DatePickerInput2 
                                value={date}
                                onChange={onChangeDate}
                                onAccept={onAcceptDate}
                                onError={onErrorDate}
                                error={dateError}
                            />
                            <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{dateError ? dateError : ''}</p>
                        </div>
                    </div>

                    <div className='flex flex-row items-center mt-10 justify-between'>
                        <div className='flex w-60'>
                            <RoundedButtonLink 
                                path='/backoffice-entities'
                                text={strings.cancel}
                                height={48}
                                bgColor='#E85F5C'
                                flex={true}
                            />
                        </div>

                        <div className='flex w-60'>
                            <RoundedButton 
                                onClick={goToEditEntitySkills}
                                text={strings.continue}
                                height={48}
                                bgColor='#007EB4'
                            />
                        </div>
                    
                    </div>
                
                </div>
            }

            <ToastContainer />
        </>
    )
}

export default EditEntity